import { MutableRefObject } from "react";

import * as Api from "@modules/api";
import { ThunkStatus } from "@utils/types/common";
import { Server } from "@wajeez/wajeez.microservices";

export enum SelectedPlayerContentSource {
  Details,
  Components,
  Search,
  MyLibrary,
  Playlist,
  NextTitle,
  OnRefresh,
}

export type Content = {
  id: string | undefined;
  title: string | undefined;
  author: string | undefined;
  thumbnail: string | undefined;
  contentEntityType: string | undefined;
  source: SelectedPlayerContentSource | undefined;
};

export interface ContentState {
  data: Server.ContentEntityOutput[];
  progresses: Server.ContentEntityProgressOutput[];
  linkedData: {
    [uniqueId: string]: {
      contentIds: (string | undefined)[];
      contents: Server.ContentEntityOutput[];
      status: ThunkStatus;
      moreStatus: ThunkStatus;
      pageNumber: number;
      pageSize: number;
      hasNextPage: boolean;
      totalCount: number;
    };
  };
  status: ThunkStatus;
  player: {
    isShown: boolean;
    isLoading: boolean;
    isPlaying: boolean;
    isAutoStart: boolean;
    currentTime: number;
    duration: number;
    volume: number;
    playbackRate: number;
  };
  selectedPlayerContent: {
    content: Content;
    chapters: { data: ItemContentOutputWithProgress[]; status: ThunkStatus };
    playingChapterId: string | undefined;
    firstPlayingChapterId: string | undefined;
    isPlayingChapterEnded: boolean;
    forcedPlayingChapterId: string | undefined;
  };
  visitedPlayerContent: {
    chapters: { data: Server.ItemContentOutput[]; status: ThunkStatus };
  };
  addEvent: {
    status: ThunkStatus;
  };
  getInterests: {
    data: Server.InterestOutput[];
    status: ThunkStatus;
  };
  setInterests: {
    status: ThunkStatus;
  };
  addBookmark: {
    status: ThunkStatus;
  };
  removeBookmark: {
    status: ThunkStatus;
  };
  selectedContentHighlights: {
    data: Server.HighlightOutput[];
    status: ThunkStatus;
  };
  createHighlight: { status: ThunkStatus };
  removeHighlight: { status: ThunkStatus };
  dynamicLink: {
    data: Server.GetContentDynamicLinkOutput | null;
    status: ThunkStatus;
  };
  nextContent: {
    data: Server.ContentEntityOutput | null;
    status: ThunkStatus;
  };
  playlistContentItems: {
    data: Server.PlaylistContentEntityOutput[];
    status: ThunkStatus;
  };
  searchTracked: {
    data: Api.Server.ContentEntityOutput[];
    meta: {
      hasNextPage: boolean | undefined;
      currentPage: number | undefined;
      totalCount: number | undefined;
    };
    status: ThunkStatus;
  };
  contentVoiceOvers: {
    data: Server.ContentEntityVoiceOverOutput[] | null;
    status: ThunkStatus;
  };
}

export type AudioRef = MutableRefObject<HTMLAudioElement>;

export interface ItemContentOutputWithProgress
  extends Api.Server.ItemContentOutput {
  progress?: number | undefined;
  isCompleted?: boolean | undefined;
}

export interface GetContentEntityItemsByIdInput {
  contentEntityId: string | undefined;
  narratorId?: number | undefined;
}

export interface SearchTrackedInput {
  searchText: string;
  pageNumber: number;
  pageSize: number;
  typeName?: string | undefined;
}
